import React, { Component } from 'react';
import { withRouter } from 'react-router';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import TransactionTableHeader from '../../Transactions/TransactionTableHeader';
import headers from './TransactionGroupSentReportHeaderData.json';
import getAxiosClient from '../../Utilities/get-axios-client';
import GroupReportChartOptions from '../GroupReportChartOptions.json';
import TransactionGroupSentReportTableBody from './TransactionGroupSentReportTableBody';
import SentGroupReportHeaderStats from './SentGroupReportHeaderStats';
import { defaultSearchQuery, getGroupStatsQuery } from '../TransactionGroupReportHandler';
import activateTooltip from '../../Utilities/activate-tooltip';
import TransactionModal from '../../Transactions/TransactionModalElement';
import PaginationElement from '../../Components/PaginationElement';
import TextSearch from '../../Components/TextSearch';
import TransactionGroupReportElement from '../TransactionGroupReportElement';
import { reportStartDate, reportEndDate } from '../../Utilities/date-format';

class TransactionGroupSentReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      transactionResponse: {},
      showModal: false,
      selectedTransaction: {},
      chartOptions: GroupReportChartOptions,
      searchQuery: { report_type: 'sent', ...defaultSearchQuery() },
      dateRange: {},
    };
    this.groupElementRef = React.createRef();
  }

  async componentDidMount() {
    activateTooltip();
    const dateRange = this.groupElementRef.current.dateValue;
    this.updateDateRange(dateRange);
    await this.getGroupStats(
      getGroupStatsQuery(
        'sent',
        reportStartDate(dateRange.start),
        reportEndDate(dateRange.end),
      ),
    );
  }

  async getGroupStats(groupStatsQuery) {
    try {
      const response = await getAxiosClient()
        .post('v1/transaction-groups/reports', { ...groupStatsQuery });
      this.setState({
        transactionResponse: response.data.transactions,
      });
      const days = response.data.dailyStats.map((dailyStat) => dailyStat.tstat_day);
      const sent = response.data.dailyStats.map((dailyStat) => +dailyStat.sent);
      const delivered = response.data.dailyStats.map((dailyStat) => +dailyStat.delivered);
      await this.setState((previousState) => ({
        chartOptions: {
          ...previousState.chartOptions,
          xAxis: {
            categories: days,
          },
          series: [{
            name: 'Sent',
            data: sent,
            color: '#3b5998',
          }, {
            name: 'Delivered',
            data: delivered,
            color: '#1abc9c',
          }],
        },
      }));
    } catch (e) {
      // eslint-disable-next-line
    }
  }

  showTransactionModal = async (transaction) => {
    await this.setState({ showModal: true, selectedTransaction: transaction });
  };

  getTransactions = async (searchQuery) => {
    const { dateRange } = this.state;
    const startDate = reportStartDate(dateRange.start);
    const endDate = reportEndDate(dateRange.end);
    const searchQueryUpdated = {
      ...searchQuery,
      start_date: startDate,
      end_date: endDate,
    };

    const response = await getAxiosClient().post(
      'v1/transaction/search',
      searchQueryUpdated,
      { withCredentials: true },
    );
    this.setState({ transactionResponse: response?.data ?? null });
  };

  setSearchTextValue = async (transactionSearchString, dateRange) => {
    const { searchQuery } = this.state;
    const updatedSearchQuery = {
      ...searchQuery,
      transaction_search_string: transactionSearchString,
      page: 1,
    };
    await this.setState({
      searchQuery: updatedSearchQuery,
      dateRange,
    });
    await this.getTransactions(updatedSearchQuery);
  };

  setCurrentDateRangeValue = async (dateRange) => {
    this.updateDateRange(dateRange);
    await this.getGroupStats(getGroupStatsQuery(
      'sent',
      reportStartDate(dateRange.start),
      reportEndDate(dateRange.end),
    ));
  };

  sort = async (order, orderBy) => {
    const { searchQuery } = this.state;
    const updatedSearchQuery = {
      ...searchQuery,
      order,
      order_by: orderBy,
      page: 1,
    };
    await this.setState({
      searchQuery: updatedSearchQuery,
      dateRange: this.groupElementRef.current.dateValue,
    });
    await this.getTransactions(updatedSearchQuery);
  };

  updateDateRange(dateRange) {
    this.setState((prevState) => ({
      ...prevState,
      dateRange,
    }));
  }

  render() {
    const {
      transactionResponse,
      chartOptions,
      showModal,
      selectedTransaction,
      dateRange,
      searchQuery,
    } = this.state;
    const transactionsData = transactionResponse?.data ?? [];
    return (
      <TransactionGroupReportElement
        ref={this.groupElementRef}
        setCurrentDateRangeValue={this.setCurrentDateRangeValue}
        reportType="sent"
        searchQuery={searchQuery}
      >
        <SentGroupReportHeaderStats dateRange={dateRange} />
        <div className="padding30 border-light top-buffer30">
          <h4 className="bottom-buffer30 top-buffer0">Activity</h4>
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
          />
        </div>
        <div>
          <div className="row top-buffer30">
            <div className="col-md-3">
              <TextSearch getTransactions={this.setSearchTextValue} dateRange={dateRange} />
            </div>
          </div>
          <div className="padding10 border-light top-buffer20">
            <TransactionModal
              closeModal={this.closeModal}
              showModal={showModal}
              selectedTransaction={selectedTransaction}
            />
            <table className="table table-striped">
              <TransactionTableHeader sort={this.sort} headers={headers} />
              <TransactionGroupSentReportTableBody
                transactionsData={transactionsData}
                showTransactionModal={this.showTransactionModal}
              />
            </table>
          </div>
          <PaginationElement
            transactionsResponse={transactionResponse}
            transactionSearchQuery={searchQuery}
            getTransactions={this.getTransactions}
            dateRange={dateRange}
          />
        </div>
      </TransactionGroupReportElement>
    );
  }
}

export default withRouter(TransactionGroupSentReport);
